import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminApi } from '@/lib/api';
import { formatDate, labelFor, CONTACT_REASONS } from '@/lib/taxonomies';
import { ADMIN } from '@/constants/testIds';
import { useSeo } from '@/lib/seo';

export default function ContactInboxPage() {
  useSeo({ title: 'Contact Inbox | Admin' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.messages().then(setRows).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const open = async (row) => {
    setSelected(row);
    if (!row.read) {
      try {
        const updated = await adminApi.markMessageRead(row.id, true);
        setRows((r) => r.map((x) => (x.id === row.id ? updated : x)));
      } catch { /* ignore */ }
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await adminApi.deleteMessage(id);
      toast.success('Deleted');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="kicker">Contact Inbox</p>
        <h1 className="font-serif text-3xl tracking-tight">Messages from the contact form.</h1>
      </div>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>From</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Received</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10">Inbox is empty.</TableCell></TableRow>
              )}
              {!loading && rows.map((m) => (
                <TableRow key={m.id} data-testid={ADMIN.inboxRow} className={m.read ? '' : 'bg-[hsl(var(--accent)/0.2)]'}>
                  <TableCell>
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{m.email}</div>
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="font-normal">{labelFor(CONTACT_REASONS, m.reason)}</Badge></TableCell>
                  <TableCell className="max-w-md truncate">{m.message}</TableCell>
                  <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{formatDate(m.created_at)}</TableCell>
                  <TableCell className="text-right pr-6">
                    <Button variant="secondary" size="sm" onClick={() => open(m)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name}</DialogTitle>
                <DialogDescription>
                  {selected.email} · {labelFor(CONTACT_REASONS, selected.reason)} · {formatDate(selected.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="whitespace-pre-wrap text-sm text-[hsl(var(--foreground))] leading-relaxed">
                {selected.message}
              </div>
              <div className="mt-4 flex justify-between">
                <Button variant="ghost" onClick={() => remove(selected.id)}>Delete</Button>
                <a href={`mailto:${selected.email}?subject=Re:%20A%20Guy%20Who%20Hunts`}>
                  <Button>Reply via email</Button>
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
